import { Injectable } from '@angular/core';
import { Apollo, gql, MutationResult, Query } from 'apollo-angular';
import {
  EmailPreferencesInput,
  UpdateNotificationPreferencesInput,
  UserDetails,
} from '../components/login/user.model';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { loginSuccess } from '../store/user/user.actions';
import { changeMarket } from '../store/selected-market/select-market.action';
import { changeTableMarket } from '../store/table-data/table-data.actions';
import { MarketType } from '../components/bids-and-offers/lots.model';

enum LocalStorageKeys {
  RECENTLY_SEARCHED = 'recentlySearched',
}

interface LocalStorageLocations {
  locations: string[];
}

export interface UserDetailsResponse {
  user: UserDetails;
}

const UserQuery = gql`
  query CurrentUserQuery {
    user {
      _id
      createdAt
      updatedAt
      email
      first_name
      last_name
      role
      default_commodity
      market_options
      default_market_type
      default_commodity
      default_origin
      accepted_agreements
      favorable_commodities
      receive_email_notifications
      notificationPreferences {
        topics
        bids
        offers
        trades
        negotiations
        contracts
        commodities
      }
      role
      email_preferences {
        bids
        offers
        negotiations
        trades
        contracts
        commodities
      }
    }
  }
`;

const acceptAgreementsUserMutation = gql`
  mutation AcceptAppTermsMutation {
    updateUser(updateUserInput: { accepted_agreements: true }) {
      _id
      accepted_agreements
    }
  }
`;

const updateDefaultCommodityMutation = gql`
  mutation updateDefaultCommodity($data: UpdateUserInput!) {
    updateUser(updateUserInput: $data) {
      _id
      default_commodity
    }
  }
`;

const updateUserEmailPreferencesMutation = gql`
  mutation UpdateUserEmailPreferences(
    $emailsEnabled: Boolean!
    $preferences: EmailPreferencesInput
  ) {
    updateUserEmailPreferences(
      emailsEnabled: $emailsEnabled
      preferences: $preferences
    ) {
      _id
      updatedAt
    }
  }
`;

const updatePushNotificationPreferencesMutation = gql`
  mutation UpdateNotificationPreferences(
    $inputPrefs: UpdateNotificationPreferencesInput!
  ) {
    updateNotificationPreferences(input: $inputPrefs) {
      _id
      createdAt
      updatedAt
      email
      role
      first_name
      last_name
      receive_email_notifications
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  userDetailsMemory: UserDetails;
  // used for instant initialization
  private userDetails = new BehaviorSubject<UserDetails>(null);
  userDetails$: Observable<UserDetails> = this.userDetails.asObservable();

  constructor(private apollo: Apollo, private store: Store) {}

  fetchUserData() {
    this.apollo
      .query({
        query: UserQuery,
      })
      .subscribe({
        next: (res) => {
          this.userDetails.next(res.data['user']);
          this.userDetailsMemory = res.data['user'];
          this.setUserDetails(res.data['user']);
          const data = { ...res.data['user'] };
          this.store.dispatch(loginSuccess({ user: data }));
          this.store.dispatch(
            changeTableMarket({
              marketType: MarketType[res.data['user']['default_market_type']],
            })
          );
          this.store.dispatch(
            changeMarket({ market: res.data['user']['default_market_type'] })
          );
        },
      });
  }

  userDataEffect(): Observable<any> {
    return this.apollo
      .query({
        query: UserQuery,
      })
      .pipe(
        map((res) => {
          return res.data['user'];
        })
      );
  }

  checkUserAgreement(): Observable<any> {
    return this.apollo
      .query({
        query: UserQuery,
      })
      .pipe(
        map((res) => {
          return res;
        })
      );
  }

  userAcceptAppTerms(id: string): Observable<any> {
    return this.apollo.mutate({
      mutation: acceptAgreementsUserMutation,
    });
  }

  updateDefaultCommodity(
    commodity: string
  ): Observable<MutationResult<unknown>> {
    return this.apollo.mutate({
      mutation: updateDefaultCommodityMutation,
      variables: {
        data: { default_commodity: commodity, accepted_agreements: true },
      },
    });
  }

  updateFavouriteCommodities(
    commodities: string[]
  ): Observable<MutationResult<unknown>> {
    return this.apollo.mutate({
      mutation: updateDefaultCommodityMutation,
      variables: {
        data: {
          favorable_commodities: commodities,
          accepted_agreements: true,
        },
      },
    });
  }

  updateUserEmailPreferences(
    enabled: boolean,
    emailPreferencesInput: EmailPreferencesInput
  ): Observable<MutationResult<unknown>> {
    return this.apollo.mutate({
      mutation: updateUserEmailPreferencesMutation,
      variables: {
        emailsEnabled: enabled,
        preferences: emailPreferencesInput,
      },
    });
  }

  updatePushNotificationPreferences(
    data: UpdateNotificationPreferencesInput
  ): Observable<MutationResult<unknown>> {
    return this.apollo.mutate({
      mutation: updatePushNotificationPreferencesMutation,
      variables: {
        inputPrefs: data,
      },
    });
  }

  clearUserData() {
    this.userDetails.next(null);
    // Remove only auth-related keys. Do NOT use localStorage.clear() — it would
    // also wipe non-auth state like `recentlySearched` (location search history).
    localStorage.removeItem('userData');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('faqsAccessToken');
  }

  setUserDetails(data: any) {
    localStorage.setItem('userDetails', JSON.stringify(data));
  }

  addRecentlySearchedLocation(location: string): void {
    const storedLocations = localStorage.getItem(
      LocalStorageKeys.RECENTLY_SEARCHED
    );
    const searchedLocations: LocalStorageLocations = storedLocations
      ? JSON.parse(storedLocations)
      : { locations: [] };

    if (!searchedLocations.locations.includes(location)) {
      searchedLocations.locations.push(location);
      localStorage.setItem(
        LocalStorageKeys.RECENTLY_SEARCHED,
        JSON.stringify(searchedLocations)
      );
    }
  }

  getRecentlySearchedLocations(): string[] {
    const recentlySearched: LocalStorageLocations = JSON.parse(
      localStorage.getItem(LocalStorageKeys.RECENTLY_SEARCHED)
    );
    return recentlySearched ? recentlySearched.locations : [];
  }

  getUserDetails(): UserDetails {
    const obj = JSON.parse(localStorage.getItem('userDetails') || '{}');
    return obj as UserDetails;
  }

  getUserData() {
    const userData = localStorage.getItem('userData');
    return JSON.parse(userData);
  }
}
